import { create } from 'zustand';
import { servicesContainer } from '@/core/di/services-container';
import { ApplicationType, MediaType } from '../types/translation-types';
import { MediaItem, UploadSoundRequest } from '../types/sound-management-types';

const PAGE_SIZE = 50;

interface WeezmoManagementStore {
  files: MediaItem[];
  totalCount: number;
  currentPage: number;
  pageSize: number;

  isLoading: boolean;
  isUploading: boolean;
  isDeleting: boolean;
  error: string | null;
  uploadError: string | null;

  uploadDialogOpen: boolean;
  deleteDialogOpen: boolean;
  fileToDelete: MediaItem | null;

  fetchFiles: () => Promise<void>;
  uploadFile: (file: File) => Promise<void>;
  deleteFile: (mediaId: number) => Promise<boolean>;
  setCurrentPage: (page: number) => void;
  openUploadDialog: () => void;
  closeUploadDialog: () => void;
  openDeleteDialog: (item: MediaItem) => void;
  closeDeleteDialog: () => void;
  clearUploadError: () => void;
  reset: () => void;
}

const initialState = {
  files: [] as MediaItem[],
  totalCount: 0,
  currentPage: 0,
  pageSize: PAGE_SIZE,
  isLoading: false,
  isUploading: false,
  isDeleting: false,
  error: null,
  uploadError: null,
  uploadDialogOpen: false,
  deleteDialogOpen: false,
  fileToDelete: null,
};

export const useWeezmoManagementStore = create<WeezmoManagementStore>((set, get) => ({
  ...initialState,

  fetchFiles: async () => {
    const { currentPage, pageSize } = get();
    set({ isLoading: true, error: null });
    try {
      const services = servicesContainer.getServices();
      const response = await services.getSoundsUseCase.execute({
        applicationType: ApplicationType.WeezmoWeb,
        mediaType: MediaType.WeezmoJsFile,
        skip: currentPage * pageSize,
        take: pageSize,
      });
      if (response.isSuccess) {
        set({ files: response.rows, totalCount: response.count, isLoading: false });
      } else {
        set({ error: response.errorMessage || 'Failed to load files', isLoading: false });
      }
    } catch (error) {
      set({ error: error instanceof Error ? error.message : 'Failed to load files', isLoading: false });
    }
  },

  uploadFile: async (file: File) => {
    set({ isUploading: true, uploadError: null });
    try {
      const services = servicesContainer.getServices();
      const request: UploadSoundRequest = {
        file,
        applicationType: ApplicationType.WeezmoWeb,
        mediaType: MediaType.WeezmoJsFile,
        languageCode: '',
      };
      await services.uploadSoundUseCase.execute(request);
      await get().fetchFiles();
      set({ isUploading: false, uploadDialogOpen: false });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to upload file';
      set({ uploadError: message, isUploading: false });
      throw error;
    }
  },

  deleteFile: async (mediaId: number) => {
    set({ isDeleting: true });
    try {
      const services = servicesContainer.getServices();
      const response = await services.deleteSoundUseCase.execute(mediaId);
      if (response.isSuccess) {
        await get().fetchFiles();
        set({ isDeleting: false, deleteDialogOpen: false, fileToDelete: null });
        return true;
      } else {
        set({ isDeleting: false, error: response.errorMessage || 'Failed to delete file' });
        return false;
      }
    } catch (error) {
      set({ isDeleting: false, error: error instanceof Error ? error.message : 'Failed to delete file' });
      return false;
    }
  },

  setCurrentPage: (page: number) => set({ currentPage: page }),

  openUploadDialog: () => set({ uploadDialogOpen: true, uploadError: null }),

  closeUploadDialog: () => set({ uploadDialogOpen: false, uploadError: null }),

  openDeleteDialog: (item: MediaItem) => set({ deleteDialogOpen: true, fileToDelete: item }),

  closeDeleteDialog: () => set({ deleteDialogOpen: false, fileToDelete: null }),

  clearUploadError: () => set({ uploadError: null }),

  reset: () => set(initialState),
}));
